import type { NullableMaybe, Nullable } from "types/utilities";
import { LOCALES, DEFAULT_LOCALE } from "appConfig";
import getI18nInstance from "@/utilities/i18n";

export const hasLocale = (
  to: NullableMaybe<string>,
  currentLocale: Nullable<string>,
) => {
  // check if given link already has locale
  if (
    typeof window === "undefined" ||
    currentLocale === window.___defaultLocale ||
    !to
  ) {
    return true;
  }

  return to.startsWith(`/${currentLocale}`);
};

type AppLocale = "en-AU" | "en-US" | "en-CA"; // only doing this to avoid modifying appConfig to make it a ts file
export type LocaleValue = Lowercase<AppLocale>;
export const validLocales = LOCALES.map(
  (value) => value.toLowerCase() as LocaleValue,
);

const validLocaleSet = new Set<string>(validLocales);

export function isLocaleValue(raw: string): raw is LocaleValue {
  return validLocaleSet.has(raw);
}

function toLocaleValue(raw: string | undefined): LocaleValue {
  if (!raw) return "en-us";
  const lower = raw.toLowerCase();
  return isLocaleValue(lower) ? lower : "en-us";
}

export const getLocale = () => toLocaleValue(getI18nInstance().language);

// Static list, no `window` read, so server and client strip the same prefix.
export const stripLocalePrefix = (pathname: string): string => {
  const [, first = "", ...rest] = pathname.split("/");

  return validLocaleSet.has(first.toLowerCase())
    ? `/${rest.join("/")}`
    : pathname;
};

// Locales casino actually builds for (matches createPages casino `locales`).
const CASINO_LOCALES = new Set<LocaleValue>(["en-us", "en-ca"]);

// Casino has no en-au pages. When a casino link is built from an en-au context
// (e.g. an NZ/CA user on holiday in AU), prefix with en-us instead so it lands
// on a real page rather than a non-existent /en-au/casino/* route.
export const resolveLinkLocale = (
  to: NullableMaybe<string>,
  locale: Nullable<string>,
): Nullable<string> => {
  if (!to || !locale) return locale;

  if (to.startsWith("/casino") && !CASINO_LOCALES.has(locale as LocaleValue)) {
    return DEFAULT_LOCALE.toLowerCase();
  }

  return locale;
};

export const MONTH_FIRST_COUNTRIES = new Set<string>(["US", "CA"]);
